<script setup lang="ts">
import { OpenJobFieldsFragment } from '~/api/graphql/generated'
import { useI18n } from 'vue-i18n'

export interface InfoBox {
  label: string
  icon: 'calendar-alt' | 'user-work' | 'map'
  value: string
}
const props = defineProps<{
  entity: OpenJobFieldsFragment
}>()

const { t } = useI18n()

const infoBoxes: InfoBox[] = [
  {
    label: t('openJob.detail.startDate'),
    icon: 'calendar-alt',
    value: props.entity?.fieldStartDateInfo ?? '',
  },
  {
    label: t('openJob.detail.employmentType'),
    icon: 'user-work',
    value: props.entity?.fieldEmploymentType ?? '',
  },
  {
    label: t('openJob.detail.location'),
    icon: 'map',
    value: props.entity?.fieldCity ?? '',
  },
]
</script>

<template>
  <Section>
    <ul
      v-animate="{ cluster: true, animation: 'vertical-reveal' }"
      class="boxes cul t-10 t-o-1 tl-12 tl-o-0 ds-10 ds-o-1"
    >
      <li v-for="(item, index) in infoBoxes" :key="index" v-animate="{ animation: 'vertical-reveal' }" class="item">
        <Icon :name="item.icon" />
        <div class="info">
          <div class="label">{{ item.label }}</div>
          <h4>{{ item.value }}</h4>
        </div>
      </li>
    </ul>
  </Section>
</template>
<style scoped lang="scss">
.section {
  @include fluid(margin-top, 30px, 64px);
}
.boxes {
  display: grid;
  gap: 36px;

  padding: 30px 24px;

  background-color: var(--c-background-medium);

  @include breakpoint(tl) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 40px;

    padding: 32px 40px 40px;
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .item {
    position: relative;

    display: flex;
    align-items: flex-start;
    gap: 30px;
    row-gap: 25px;

    color: var(--c-primary);

    h4 {
      margin-bottom: 0;
    }

    @include breakpoint(tl) {
      flex-direction: column;
      justify-content: center;
      align-items: center;

      text-align: center;
    }

    :deep(svg) {
      flex-shrink: 0;
      width: 25px;
      height: auto;

      @include breakpoint(tl) {
        width: 48px;
      }
    }

    &:last-child {
      &:after {
        display: none;
      }
    }

    &:after {
      content: '';
      position: absolute;
      z-index: 1;
      width: 100%;
      bottom: -18px;
      border-bottom: 1px solid var(--c-blue-30);
      @include breakpoint(tl) {
        width: auto;
        height: 100%;
        top: 0;
        right: -20px;
        bottom: auto;
        border-bottom: none;
        border-right: 1px solid var(--c-blue-30);
      }
    }
  }
}
</style>
